/**
 * ClustersViewStep - Step 3 (Path B): View and select clusters
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Layers, AlertCircle, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ClusterSummary, INTENT_LABELS, INTENT_COLORS } from '@/types/contentPlanner';
import { useQuery } from '@tanstack/react-query';
import { contentPlannerService } from '@/services/contentPlannerService';
import { useTenantStore } from '@/stores/useTenantStore';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from 'antd';

interface ClustersViewStepProps {
  mapId: string | null;
  selectedClusters: ClusterSummary[];
  onSelectClusters: (clusters: ClusterSummary[]) => void;
}

const ClustersViewStep: React.FC<ClustersViewStepProps> = ({
  mapId,
  selectedClusters,
  onSelectClusters,
}) => {
  const { currentTenant } = useTenantStore();

  const { data: mapDetail, isLoading } = useQuery({
    queryKey: ['topical-map', currentTenant, mapId],
    queryFn: () => contentPlannerService.getMap(mapId!),
    enabled: !!currentTenant && !!mapId,
  });

  const allClusters = mapDetail?.clusters || [];
  const selectedIds = new Set(selectedClusters.map(c => c.id));

  const handleToggleCluster = (cluster: ClusterSummary) => {
    if (selectedIds.has(cluster.id)) {
      onSelectClusters(selectedClusters.filter(c => c.id !== cluster.id));
    } else {
      onSelectClusters([...selectedClusters, cluster]);
    }
  };



  const handleDeselectAll = () => {
    onSelectClusters([]);
  };

  const getDifficultyColor = (kd: number) => {
    if (kd <= 30) return '#52c41a';
    if (kd <= 50) return '#faad14';
    if (kd <= 70) return '#fa8c16';
    return '#f5222d';
  };

  const getDifficultyLabel = (kd: number) => {
    if (kd <= 30) return 'Very easy';
    if (kd <= 50) return 'Easy';
    if (kd <= 70) return 'Medium';
    return 'Hard';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2">Select clusters</h2>
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!mapDetail || allClusters.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2">Select clusters</h2>
          <p className="text-sm text-muted-foreground">
            No research data found. Please go back and start a new research session.
          </p>
        </div>
        <div className="flex items-center gap-2 p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-700 dark:text-amber-400">
            No clusters were found in this research session. Try a different seed keyword.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Select topic clusters</h2>
        <p className="text-sm text-muted-foreground">
          Choose which clusters you want to create content for. Each cluster will become one blog post.
        </p>
      </div>

      {/* Stats and bulk actions */}
      <div className="flex items-center justify-between p-3 bg-muted rounded-lg border">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium">
              {selectedClusters.length} of {allClusters.length} selected
            </span>
          </div>
          {selectedClusters.length > 0 && (
            <Badge variant="secondary">
              {selectedClusters.reduce((sum, c) => sum + c.total_volume, 0).toLocaleString()} total volume
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSelectAll}>
            Select All
          </Button>
          {selectedClusters.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleDeselectAll}>
              Deselect All
            </Button>
          )}
        </div>
      </div>

      {/* Clusters list */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {allClusters.map((cluster) => {
          const isSelected = selectedIds.has(cluster.id);

          return (
            <div
              key={cluster.id}
              className={cn(
                'p-4 rounded-lg border transition-all cursor-pointer',
                isSelected
                  ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-700 shadow-sm'
                  : 'bg-card border-border hover:border-blue-200 hover:shadow-sm'
              )}
              onClick={() => handleToggleCluster(cluster)}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleToggleCluster(cluster)}
                  className="mt-0.5"
                />

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{cluster.name}</span>
                    <Tag color={INTENT_COLORS[cluster.primary_intent]} className="text-xs">
                      {INTENT_LABELS[cluster.primary_intent]}
                    </Tag>
                  </div>



                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-muted-foreground">
                      Keywords: <span className="font-medium text-blue-600 dark:text-blue-400">{cluster.keyword_count}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Volume: <span className="font-medium">{cluster.total_volume.toLocaleString()}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Difficulty:{' '}
                      <span
                        className="font-medium"
                        style={{ color: getDifficultyColor(cluster.avg_difficulty) }}
                      >
                        {getDifficultyLabel(cluster.avg_difficulty)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {selectedClusters.length > 0 && (
        <div className="p-3 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-green-700 dark:text-green-400">
            <span className="font-medium">{selectedClusters.length} blog post{selectedClusters.length !== 1 ? 's' : ''}</span>
            {' '}will be created from the selected clusters
          </p>
        </div>
      )}
    </div>
  );
};

export default ClustersViewStep;
